import React, { useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

// Load the zoom module
import HC_more from 'highcharts/highcharts-more';
import { round } from '@turf/turf';
import { parseISODate } from '../../../Helpers/utils';
HC_more(Highcharts);

const SoilMetricsGraphs = ({ soilData, supportedSensors, selectedPoint, isAdminUser }) => {

  const chartRefs = useRef({});
  const isZoomingSynced = useRef(false);
  const isHoverSynced = useRef(false);
  
  let hpfp_percent;
  let hcc_percent;

  useEffect(() => {
    Object.values(chartRefs.current).forEach(chartRef => {
      if (chartRef && chartRef.chart) {
        const chart = chartRef.chart;
        chart.yAxis.forEach(axis => {
          if (axis.options.title.text === 'Humidité (%)') {
            axis.setExtremes();
          }
        });
      }
    });
  }, [soilData, hpfp_percent, hcc_percent]);

  if (selectedPoint && selectedPoint.hpfp_percent !== null && selectedPoint.hpfp_percent !== undefined && selectedPoint.hpfp_percent !== '') {
    hpfp_percent = Number(selectedPoint.hpfp_percent);
  }

  if (selectedPoint && selectedPoint.hcc_percent !== null && selectedPoint.hcc_percent !== undefined && selectedPoint.hcc_percent !== '') {
    hcc_percent = Number(selectedPoint.hcc_percent);
  }

  const syncExtremes = (e) => {
    if (isZoomingSynced.current) return;
    
    const thisChart = e.target.chart;
    isZoomingSynced.current = true;

    Object.values(chartRefs.current).forEach(chartRef => {
      if (chartRef && chartRef.chart && chartRef.chart !== thisChart) {
        const chart = chartRef.chart;
        if (chart.xAxis && chart.xAxis[0]) {
          if (e.min === undefined || e.max === undefined) {
            chart.xAxis[0].setExtremes(null, null);
          } else {
            chart.xAxis[0].setExtremes(e.min, e.max, true, false);
          }
        }
      }
    });

    isZoomingSynced.current = false;
  };

  const syncHover = (sourceChart, x) => {
    if (isHoverSynced.current) return;
    
    isHoverSynced.current = true;

    Object.values(chartRefs.current).forEach(chartRef => {
      if (chartRef && chartRef.chart && chartRef.chart !== sourceChart) {
        const chart = chartRef.chart;
        const series = chart.series;
        let point = null;
        
        // Find the closest point to the x value
        for (let i = 0; i < series.length; i++) {
          const closestPoint = series[i].searchPoint({ chartX: chart.xAxis[0].toPixels(x) }, true);
          if (closestPoint) {
            point = closestPoint;
            break;
          }
        }
        
        if (point) {
          chart.tooltip.refresh(chart.series.map(s => {
            return s.searchPoint({ chartX: chart.xAxis[0].toPixels(x) }, true);
          }).filter(p => p));
          chart.xAxis[0].drawCrosshair(null, point);
        }
      }
    });

    isHoverSynced.current = false;
  };

  const syncHoverOut = () => {
    if (isHoverSynced.current) return;
    
    isHoverSynced.current = true;

    Object.values(chartRefs.current).forEach(chartRef => {
      if (chartRef && chartRef.chart) {
        chartRef.chart.tooltip.hide();
        chartRef.chart.xAxis[0].hideCrosshair();
      }
    });

    isHoverSynced.current = false;
  };

  const renderLineChart = (dataKeys, name, colors) => {
    const sortedSoilData = [...soilData].sort((a, b) => parseISODate(a.reception_date) - parseISODate(b.reception_date));
    
    // Calculate date range to determine if we should show time
    const dateRange = sortedSoilData.length > 0 ? 
      parseISODate(sortedSoilData[sortedSoilData.length - 1].reception_date) - parseISODate(sortedSoilData[0].reception_date) : 0;
    const showTime = dateRange < (3 * 24 * 60 * 60 * 1000); // Less than 3 days
    
    // Handle single value vs array of values
    const series = dataKeys.map((dataKey, index) => {
      const dataKeyName = Array.isArray(dataKey) ? dataKey[0] : dataKey;
      const seriesName = dataKeys.length > 1 ? 
        `${name} ${index === 0 ? '(Niveau 1)' : '(Niveau 2)'}` : 
        name;

      // Create data array with null values for gaps > 1 hour
      const rawData = sortedSoilData
        .map(d => [
          parseISODate(d.reception_date), 
          round(d[dataKeyName] !== null && d[dataKeyName] !== undefined ? d[dataKeyName] : null, 2)
        ])
        .filter(d => d[1] !== null);

      const dataWithGaps = [];
      const GAP_THRESHOLD = 3600000; // 1 hour in milliseconds

      for (let i = 0; i < rawData.length; i++) {
        dataWithGaps.push(rawData[i]);
        
        // Check if there's a next point and if the gap is larger than threshold
        if (i < rawData.length - 1) {
          const timeDiff = rawData[i + 1][0] - rawData[i][0];
          if (timeDiff > GAP_THRESHOLD) {
            // Insert a null point to break the line
            dataWithGaps.push([rawData[i][0] + 1, null]);
          }
        }
      }

      return {
        name: seriesName,
        type: 'spline',
        data: dataWithGaps,
        color: colors[index],
      };
    });

    const adjustAxisRange = (value, currentMin, currentMax, padding = 5) => {
      if (value < currentMin) {
        return Math.max(0, value - padding);
      }
      if (value > currentMax) {
        return value + padding;
      }
      return null;
    };

    // Add special plot lines for battery level
    const getBatteryPlotLines = () => {
      if (name.includes('Batterie') || name.includes('Battery')) {
        return [
          {
            color: 'red',
            dashStyle: 'dash',
            value: 20,
            width: 2,
            label: { text: 'Niveau critique (20%)', align: 'right', style: { color: 'red' } }
          },
          {
            color: 'orange',
            dashStyle: 'dash',
            value: 50,
            width: 2,
            label: { text: 'Niveau bas (50%)', align: 'right', style: { color: 'orange' } }
          }
        ];
      }
      return [];
    };

    const options = {
      chart: {
        zoomType: 'xy',
        height: '500px',
        resetZoomButton: {
          position: {
            align: 'right',
            verticalAlign: 'top',
            x: -10,
            y: 10
          }
        }
      },
      title: {
        text: `${name} au fil du temps`,
      },
      xAxis: {
        type: 'datetime',
        labels: {
          formatter: function () {
            if (showTime) {
              return Highcharts.dateFormat('%d/%m %H:%M', this.value);
            }
            return Highcharts.dateFormat('%d/%m', this.value);
          }
        },
        events: {
          afterSetExtremes: syncExtremes
        },
        crosshair: true
      },
      yAxis: {
        title: {
          text: name,
        },
        events: {
          afterSetExtremes: function(e) {
            if (this.options.title.text !== 'Humidité (%)') return;
      
            const axis = this;
            const dataMin = axis.dataMin;
            const dataMax = axis.dataMax;
      
            let newMin = dataMin;
            let newMax = dataMax;
      
            if (hpfp_percent) {
              newMin = Math.min(newMin, adjustAxisRange(hpfp_percent, dataMin, dataMax) || dataMin);
              newMax = Math.max(newMax, adjustAxisRange(hpfp_percent, dataMin, dataMax) || dataMax);
            }
            if (hcc_percent) {
              newMin = Math.min(newMin, adjustAxisRange(hcc_percent, dataMin, dataMax) || dataMin);
              newMax = Math.max(newMax, adjustAxisRange(hcc_percent, dataMin, dataMax) || dataMax);
            }
      
            if (newMin !== e.min || newMax !== e.max) {
              axis.setExtremes(newMin, newMax);
            }
          }
        },
        plotLines: [
          ...(hpfp_percent && name === 'Humidité (%)' ? [{
            color: 'red',
            dashStyle: 'dash',
            value: hpfp_percent,
            width: 2,
            label: { text: 'HPFP', align: 'right', style: { color: 'red' } }
          }] : []),
          ...(hcc_percent && name === 'Humidité (%)' ? [{
            color: 'blue',
            dashStyle: 'dash',
            value: hcc_percent,
            width: 2,
            label: { text: 'HCC', align: 'right', style: { color: 'blue' } }
          }] : []),
          ...getBatteryPlotLines()
        ].filter(Boolean)
      },
      legend: {
        enabled: true,
      },
      tooltip: {
        shared: true,
        crosshairs: true,
        formatter: function () {
          const date = Highcharts.dateFormat('%e %b %Y %H:%M', this.x);
          let tooltip = `<b>${date}</b><br/>`;

          this.points.forEach(point => {
            tooltip += `<span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${point.y}</b><br/>`;
          });

          return tooltip;
        }
      },
      plotOptions: {
        series: {
          connectEnds: false,
          marker: {
            enabled: false
          },
          point: {
            events: {
              mouseOver: function() {
                const chart = this.series.chart;
                syncHover(chart, this.x);
              },
              mouseOut: function() {
                syncHoverOut();
              }
            }
          },
          events: {
            mouseOut: function() {
              syncHoverOut();
            }
          }
        }
      },
      series: series,
    };

    return (
      <div className="w-full p-12" key={name}>
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          ref={(chart) => chartRefs.current[name] = chart}
        />
      </div>
    );
  };

  const getChartConfig = (sensor) => {
    const lvl1_color = '#ff0000ff'
    const lvl2_color = '#05b0ffff'

    switch (sensor) {
      case 'EC':
        return {
          dataKeys: ['ec_1', 'ec_2'],
          name: 'Conductivité électrique (µS/cm)',
          colors: [lvl1_color, lvl2_color]
        };
      case 'Hum':
        return {
          dataKeys: ['hum_1', 'hum_2'],
          name: 'Humidité (%)',
          colors: [lvl1_color, lvl2_color]
        };
      case 'Temp':
        return {
          dataKeys: ['temp_1', 'temp_2'],
          name: 'Température (°C)',
          colors: [lvl1_color, lvl2_color]
        };
      case 'N':
        return {
          dataKeys: ['N'],
          name: 'Azote (mg/kg)',
          colors: ['#0088FE']
        };
      case 'pH':
        return {
          dataKeys: ['ph'],
          name: 'pH',
          colors: ['#ffc658']
        };
      case 'P':
        return {
          dataKeys: ['P'],
          name: 'Phosphore (mg/kg)',
          colors: ['#00C49F']
        };
      case 'K':
        return {
          dataKeys: ['K'],
          name: 'Potassium (mg/kg)',
          colors: ['#FFBB28']
        };
      case 'Battery':
        return {
          dataKeys: ['battery_level', 'battery_lvl'].filter(key => 
            soilData.some(d => d[key] !== null && d[key] !== undefined)
          ).slice(0, 1), // Take the first available field
          name: 'Niveau de batterie (%)',
          colors: ['#FF6B6B']
        };
      case 'SensorTemp':
        return {
          dataKeys: ['sensor_temp', 'sensor_temperature'].filter(key => 
            soilData.some(d => d[key] !== null && d[key] !== undefined)
          ).slice(0, 1), // Take the first available field
          name: 'Température du capteur (°C)',
          colors: ['#4ECDC4']
        };
      default:
        return null;
    }
  };

  const renderCharts = () => {
    const sharedSensors = [];
    const charts = [];
    const { lvl1, lvl2 } = supportedSensors;

    const checkAndAddSharedSensor = (sensor, sharedSensorsList) => {
      if (lvl2.includes(sensor) && !sharedSensorsList.includes(sensor)) {
        sharedSensorsList.push(sensor);
        return true;
      }
      return false;
    };

    const renderSensorChart = (sensor, dataKeyIndex) => {
      const config = getChartConfig(sensor);
      if (config && config.dataKeys.length > 0) {
        const dataKeys = [config.dataKeys[dataKeyIndex] || config.dataKeys[0]];
        const colors = [config.colors[dataKeyIndex] || config.colors[0]];
        charts.push(renderLineChart(dataKeys, config.name, colors));
      }
    };

    // Render level 1 sensors
    lvl1.forEach(sensor => {
      if (sensor && !checkAndAddSharedSensor(sensor, sharedSensors)) {
        renderSensorChart(sensor, 0);
      }
    });

    // Render level 2 sensors
    lvl2.forEach(sensor => {
      if (sensor && !sharedSensors.includes(sensor)) {
        renderSensorChart(sensor, 1);
      }
    });

    // Render shared sensors (both levels)
    sharedSensors.forEach(sensor => {
      const config = getChartConfig(sensor);
      if (config && config.dataKeys.length > 0) {
        charts.push(renderLineChart(config.dataKeys, config.name, config.colors));
      }
    });

    // Add battery and sensor temperature charts if data exists
    const hasBatteryData = soilData.some(d => 
      (d.battery_level !== null && d.battery_level !== undefined) || 
      (d.battery_lvl !== null && d.battery_lvl !== undefined)
    );
    
    const hasSensorTempData = soilData.some(d => 
      (d.sensor_temp !== null && d.sensor_temp !== undefined) || 
      (d.sensor_temperature !== null && d.sensor_temperature !== undefined)
    );

    if (isAdminUser && hasBatteryData) { // Check isAdminUser here
      const batteryConfig = getChartConfig('Battery');
      if (batteryConfig && batteryConfig.dataKeys.length > 0) {
        charts.push(renderLineChart(batteryConfig.dataKeys, batteryConfig.name, batteryConfig.colors));
      }
    }

    if (isAdminUser && hasSensorTempData) { // Check isAdminUser here
      const sensorTempConfig = getChartConfig('SensorTemp');
      if (sensorTempConfig && sensorTempConfig.dataKeys.length > 0) {
        charts.push(renderLineChart(sensorTempConfig.dataKeys, sensorTempConfig.name, sensorTempConfig.colors));
      }
    }

    return charts;
  };

  if (!soilData.length) {
    return <div className="text-center text-xl font-semibold">Pas de données à afficher</div>;
  }

  return (
    <div className="container mx-auto p-4">
      {renderCharts()}
    </div>
  );
};

export default SoilMetricsGraphs;